/**
 * Artifact provenance extraction + Sources-registry unification.
 *
 * Each artifact spec (SAR table, Plotly chart, network graph) may carry:
 *   - spec.sources: [{ id, dataset, plexId, url, title, record }]
 *     A top-level registry of sources cited anywhere in the artifact.
 *
 *   - Row-level attribution:
 *     SAR     → row.source_id  (existing field)
 *     Plotly  → trace._source  (one per trace, applied to all its points)
 *     Network → node._source
 *
 *   - Column-level attribution:
 *     SAR     → column.source_id  (new — column header carries its source)
 *     Plotly  → trace._source serves both (a trace IS a column in this model)
 *     Network → graph.edgeTypeSources: { edgeType: source_id }
 *
 *   - Optional sidecar (used when inline is awkward):
 *     spec.provenance: { rows: { 0: "s1", … }, columns: { ic50: "s2", … } }
 *
 * Source identity: spec.sources[] entries should carry `url` (HTTP) so
 * the FE can register them into the shared SourceReferencesStore. The
 * store dedupes by URL — registering an artifact source that the chat
 * already cited returns the same store-side id, and clicking a chip in
 * the artifact highlights the same row in the Sources panel.
 *
 * Backward compat: pre-existing artifacts (no `sources` / no `_source`)
 * extract to an empty map — UI renders nothing. Silent fallback.
 */

import { useCallback, useEffect, useMemo, useRef } from "react";

/**
 * Pull the provenance shape out of a spec, agnostic to artifact kind.
 * Returns:
 *   {
 *     sources:        Array<{id, dataset, plexId, url, title, record}>,
 *     bySpecId:       Map<specSourceId, sourceObject>
 *     rowSourceId:    (row, idx) => specSourceId | null
 *     columnSourceId: (column, key) => specSourceId | null
 *   }
 */
export const extractProvenance = (spec) => {
  const sources = Array.isArray(spec?.sources) ? spec.sources : [];
  const bySpecId = new Map();
  sources.forEach((s) => { if (s?.id) bySpecId.set(s.id, s); });

  const provenance = spec?.provenance || {};
  const rowsMap = provenance.rows || {};
  const columnsMap = provenance.columns || {};
  const tracesMap = provenance.traces || {};              // plotly sidecar
  const edgeTypesMap = provenance.edgeTypes || {};        // network sidecar
  const edgeTypeSources = spec?.graph?.edgeTypeSources || {}; // network inline
  const nodesMap = provenance.nodes || {};               // network node sidecar

  const rowSourceId = (row, idx) => {
    if (row?.source_id) return row.source_id;
    if (row?._source) return row._source;
    if (rowsMap[idx] != null) return rowsMap[idx];
    if (nodesMap[idx] != null) return nodesMap[idx];
    if (row?.id != null && nodesMap[row.id] != null) return nodesMap[row.id]; // network node sidecar is keyed by node id
    return null;
  };

  const columnSourceId = (column, key) => {
    if (column?.source_id) return column.source_id;
    if (column?.source) return column.source;
    const k = key ?? column?.key ?? column?.id;
    if (k == null) return null;
    if (edgeTypeSources[k] != null) return edgeTypeSources[k];   // network inline
    if (columnsMap[k] != null) return columnsMap[k];             // sar sidecar
    if (tracesMap[k] != null) return tracesMap[k];               // plotly sidecar
    if (edgeTypesMap[k] != null) return edgeTypesMap[k];         // network sidecar
    return null;
  };

  return { sources, bySpecId, rowSourceId, columnSourceId };
};

/**
 * Best-effort URL builder for sources missing one. Returns null if we
 * can't construct a stable URL — the registry will simply skip those.
 * Mirrors the URL patterns ChEMBL / UniProt / NCBI use today; non-
 * authoritative but lets old specs benefit from chip unification.
 *
 * `synthetic: true` adds a final fallback for plexId-only sources whose
 * prefix has no canonical external card (e.g. `assay:`, `geneset:`): a
 * stable synthetic key so the chip still converges on a Sources-panel
 * row. It MUST be an https URL — SourceReferencesStore.addSource rejects
 * anything matching RE_PLEX_ID (its lookahead exempts only http(s):), so
 * a bare `plex:<id>` scheme would be dropped. Callers only opt in at
 * click time (not during eager registration) so the panel isn't padded
 * with synthetic rows the user never asked to see.
 */
const urlForSource = (src, { synthetic = false } = {}) => {
  if (!src) return null;
  if (src.url) return src.url;
  const plexId = src.plexId || src.plex_id || "";
  if (!plexId || typeof plexId !== "string") return null;
  const [prefix, rawValue] = plexId.split(":", 2);
  if (!prefix || !rawValue) return null;
  const value = encodeURIComponent(rawValue);
  switch (prefix.toLowerCase()) {
    case "compound":
    case "chembl":
      return `https://www.ebi.ac.uk/chembl/compound_report_card/${value}`;
    case "target":
    case "entrezgene":
    case "gene":
      return `https://www.ncbi.nlm.nih.gov/gene/${value}`;
    case "hgnc":
      return `https://www.genenames.org/data/gene-symbol-report/#!/symbol/${value}`;
    case "uniprot":
      return `https://www.uniprot.org/uniprot/${value}`;
    default:
      return synthetic ? `https://www.plexsearch.com/id/${encodeURIComponent(plexId)}` : null;
  }
};

/**
 * React hook: registers all spec.sources into the shared
 * SourceReferencesStore and returns a stable map from spec-source-id
 * to store-side source id (e.g. "source-7"). Use the store id when
 * calling `store.scrollToSource()` so the Sources panel can highlight
 * the same row.
 *
 * Registration happens once per (spec, store) tuple — observer
 * components re-running the hook with the same spec won't re-register.
 *
 * `turnId` / `agentId` are passed to addSource so the store keeps
 * provenance of which turn surfaced which source.
 */
export const useArtifactSourceRegistry = (
  spec,
  sourceReferencesStore,
  { turnId = "v2-artifact", agentId = null } = {},
) => {
  const provenance = useMemo(() => extractProvenance(spec), [spec]);
  // Persistent spec-source-id → store-source-id map. Deliberately NOT
  // reset on every effect run: streaming re-emits the same artifact spec
  // with a fresh object identity, which used to blow this map away and
  // make storeIdFor() return null mid-click — a dead chip / disabled
  // button (PLEX-4228). We merge into the same ref instead; addSource is
  // idempotent (dedupes by URL) so re-registration is a no-op.
  const mapRef = useRef(new Map());

  // Resolve — and, when missing, register on demand — the store id for a
  // spec source. Safe to call at click time: because addSource dedupes by
  // URL, repeated calls return the same store id. Returns null only when
  // the source has no registerable URL/plexId at all. `synthesize` opts in
  // to the plexId-only synthetic key (see urlForSource) — click handlers
  // pass true so a chip for an unrecognized-prefix source still lands on a
  // Sources row rather than silently no-op'ing.
  const resolveStoreId = useCallback((specSourceId, { synthesize = false } = {}) => {
    if (!specSourceId) return null;
    const cached = mapRef.current.get(specSourceId);
    if (cached) return cached;
    if (!sourceReferencesStore?.addSource) return null;
    const src = provenance.bySpecId.get(specSourceId);
    if (!src) return null;
    const url = urlForSource(src, { synthetic: synthesize });
    if (!url) return null;
    const title = src.title || src.record || src.plexId || src.dataset || url;
    const storeId = sourceReferencesStore.addSource(url, title, turnId, agentId);
    if (storeId) mapRef.current.set(specSourceId, storeId);
    return storeId || null;
  }, [provenance, sourceReferencesStore, turnId, agentId]);

  // Eagerly register every source that already has a real/canonical URL so
  // the Sources panel lists them up front. Populates the SAME persistent
  // map (never resets it). Synthetic keys are left to on-demand click
  // resolution so the panel isn't padded with them.
  useEffect(() => {
    if (!sourceReferencesStore?.addSource) return;
    provenance.sources.forEach((src) => {
      if (src?.id) resolveStoreId(src.id);
    });
  }, [provenance, sourceReferencesStore, resolveStoreId]);

  // Pure read for render-time use (no observable mutation → safe inside an
  // observer's render). Click handlers should prefer resolveStoreId, which
  // registers on demand.
  const storeIdFor = useCallback(
    (specSourceId) => (specSourceId ? mapRef.current.get(specSourceId) || null : null),
    [],
  );

  return {
    ...provenance,
    storeIdFor,
    resolveStoreId,
  };
};

/**
 * Display helper — pulls a short display label for a source. Prefers
 * the dataset name; falls back to plexId; then title; then URL host.
 */
export const sourceLabel = (src) => {
  if (!src) return "source";
  if (src.dataset) return src.dataset;
  if (src.plexId) {
    const [prefix] = src.plexId.split(":", 2);
    return prefix || src.plexId;
  }
  if (src.title) return src.title;
  if (src.url) {
    try { return new URL(src.url).hostname.replace(/^www\./, ""); }
    catch (_) { /* fall through */ }
  }
  return "source";
};

/**
 * Icon glyph per dataset / category. Falls back to 📎 if unknown.
 */
export const sourceIcon = (src) => {
  const ds = (src?.dataset || "").toLowerCase();
  if (ds.includes("chembl")) return "🧪";
  if (ds.includes("depmap")) return "📊";
  if (ds.includes("uniprot")) return "🧬";
  if (ds.includes("pubchem")) return "🧪";
  if (ds.includes("ncbi") || ds.includes("entrez")) return "🧬";
  if (ds.includes("ensembl")) return "🧬";
  if (ds.includes("reactome") || ds.includes("kegg")) return "🛤";
  return "📎";
};
